// ================================================================
// Geister-Zeiger — der Assistent tippt SICHTBAR auf die Oberfläche
// ================================================================
// Bisher sprang die App auf Zuruf einfach um: gesagt «Einstellungen öffnen»,
// und der Bildschirm war ein anderer. Was dazwischen passiert ist, blieb
// unsichtbar — man musste dem Assistenten glauben, dass er das Richtige
// getroffen hat.
//
// Jetzt fährt ein runder Zeiger sichtbar zum Knopf und tippt ihn an, wie der
// Touch-Kreis in der Handy-Ansicht der Chrome-Entwicklerwerkzeuge. Das ist
// nicht Dekoration: es zeigt, WO die App gleich hinfasst, und macht einen
// Fehlgriff sofort erkennbar («er geht auf den falschen Knopf») statt erst am
// Ergebnis.
//
// Bewusst reines DOM ausserhalb von React: die Zeiger-Schicht muss über JEDER
// Oberfläche liegen (Handy-Shell, Dashboard, Sheets im Portal) und darf beim
// Neuzeichnen einer Insel nicht verschwinden. Ausserdem laufen die
// UI-Kommandos in Vanilla-Handlern.

const DOT_ID = 'lu-ghost-pointer';
const MOVE_MS = 520;   // Weg zum Ziel — langsam genug, um ihm zu folgen
const TAP_MS = 260;    // Antippen (Ring geht auf und verblasst)
const REST_MS = 260;   // kurz liegen bleiben, damit man sieht, WO getippt wurde

let dot = null;
let lastX = 0;
let lastY = 0;

function ensureDot() {
  if (dot && dot.isConnected) return dot;
  dot = document.createElement('div');
  dot.id = DOT_ID;
  dot.className = 'lu-ghost';
  dot.innerHTML = '<span class="lu-ghost-ring"></span>';
  document.body.appendChild(dot);
  return dot;
}

/** Mitte eines Elements in Viewport-Koordinaten — oder null, wenn unsichtbar. */
function centerOf(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // Nullfläche = ausgeblendet oder noch nicht gelayoutet: dann lieber gar
  // keinen Zeiger zeigen, als ihn in die Ecke fahren zu lassen.
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/**
 * Zeiger sichtbar zum Element fahren und antippen.
 *
 * Gibt ein Promise zurück, das NACH der Tipp-Animation auflöst — der Aufrufer
 * führt die eigentliche Aktion danach aus, damit Bild und Wirkung
 * zusammenpassen. Findet er das Ziel nicht, löst er sofort auf: die Aktion
 * soll nie an der Animation scheitern.
 *
 * `prefers-reduced-motion` wird respektiert — dann gibt es keinen Flug, nur
 * ein kurzes Aufblitzen am Ziel.
 */
export function ghostTap(target) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  const to = centerOf(el);
  if (!to) return Promise.resolve(false);

  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const node = ensureDot();

  // Startpunkt: wo der Zeiger zuletzt stand, sonst die Bildschirmmitte —
  // ein Flug von (0,0) sähe aus wie ein Fehler.
  if (!lastX && !lastY) {
    lastX = window.innerWidth / 2;
    lastY = window.innerHeight / 2;
  }

  node.style.transition = 'none';
  node.style.transform = `translate(${lastX}px, ${lastY}px)`;
  node.classList.add('on');
  node.classList.remove('tap');

  // Reflow erzwingen, damit der Startpunkt wirklich gilt — sonst interpoliert
  // der Browser vom vorherigen Wert und der Flug beginnt falsch.
  //
  // BEWUSST kein requestAnimationFrame: in einem Hintergrund-Tab feuert rAF
  // gar nicht. Das Versprechen löste dann NIE auf — und weil der Aufrufer die
  // eigentliche Aktion daran hängt, wäre «Einstellungen öffnen» in einem
  // nicht sichtbaren Tab einfach nie passiert. Ein Lesezugriff auf
  // offsetWidth erzwingt denselben Effekt synchron und immer.
  void node.offsetWidth;

  return new Promise((resolve) => {
    node.style.transition = reduced ? 'none' : `transform ${MOVE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
    node.style.transform = `translate(${to.x}px, ${to.y}px)`;
    lastX = to.x;
    lastY = to.y;

    const fly = reduced ? 0 : MOVE_MS;
    setTimeout(() => {
      node.classList.add('tap');
      setTimeout(() => {
        node.classList.remove('tap');
        setTimeout(() => {
          node.classList.remove('on');
          resolve(true);
        }, REST_MS);
      }, TAP_MS);
    }, fly);
  });
}

/** Zeiger sofort ausblenden (z.B. wenn der Nutzer selbst eingreift). */
export function ghostHide() {
  if (dot) dot.classList.remove('on', 'tap');
}
